import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { City, Province } from '@interfaces/Locations';
import { LocationService } from '@service-features/location.service';
import { isStringEntity } from '@utils/helpers';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { InputBusyIndicatorComponent } from '../input-busy-indicator/input-busy-indicator.component';
import { FORM_IMPORTS } from 'app/shared/imports/form.imports';

@Component({
  selector: 'gd-location-selector',
  templateUrl: './location-selector.component.html',
  styleUrls: ['./location-selector.component.scss'],
  standalone: true,
  imports: [
    ...FORM_IMPORTS,
    MatTooltipModule,
    MatAutocompleteModule,
    InputBusyIndicatorComponent,
  ],
})
export class LocationSelectorComponent implements OnInit {
  @Input() provinceControl!: FormControl;
  @Input() cityControl!: FormControl;

  provinces!: Province[];
  cities!: City[];

  filteredProvinces$!: Observable<Province[]>;
  filteredCities$!: Observable<City[]>;

  get provincesBusy$(): Observable<boolean> {
    return this.locationService.provincesBusy$;
  }

  get citiesBusy$(): Observable<boolean> {
    return this.locationService.citiesBusy$;
  }

  constructor(private locationService: LocationService) {
    this.locationService.loadProvinces();
    this.locationService.loadCities();
  }

  ngOnInit(): void {
    this.loadProvinces();
    this.loadCities();
    this.filteredProvinces$ = this.provinceControl.valueChanges.pipe(
      startWith(''),
      map((value) => (isStringEntity(value) ? value : value?.name || '')),
      switchMap((value) => this._filterProvinces(value || '')),
    );

    this.filteredCities$ = this.cityControl.valueChanges.pipe(
      startWith(''),
      map((value) => (isStringEntity(value) ? value : value?.name || '')),
      switchMap((value) => this._filterCities(value || '')),
    );
  }

  private loadProvinces(): void {
    this.locationService.getProvinces().subscribe((provinces) => {
      this.provinces = provinces;
    });
  }

  private loadCities(): void {
    this.locationService.getCities().subscribe((cities) => {
      this.cities = cities;
    });
  }

  private _filterProvinces(value: string): Observable<Province[]> {
    const filterValue = value.toLowerCase();
    if (!filterValue) return of(this.provinces);
    return of(
      this.provinces.filter((province) =>
        province.name.toLowerCase().includes(filterValue),
      ),
    );
  }

  private _filterCities(value: string): Observable<City[]> {
    const filterValue = value.toLowerCase();
    if (!filterValue) return of(this.cities);
    return of(
      this.cities.filter((city) =>
        city.name.toLowerCase().includes(filterValue),
      ),
    );
  }

  onProvinceChange(provinceName: string): void {
    this.locationService.loadCities('', provinceName);
  }
}
